import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import SEOHead from "@/components/base/SEOHead";
import SchemaOrg from "@/components/base/SchemaOrg";
import ScrollToTop from "@/components/base/ScrollToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import { categories } from "@/mocks/categories";
import { getArticlesByCategory } from "@/mocks/articles";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = categories.find((c) => c.slug === slug);
  const categoryArticles = slug ? getArticlesByCategory(slug) : [];

  if (!currentCategory) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="flex flex-col items-center justify-center py-24 px-4">
          <div className="w-16 h-16 rounded-full bg-secondary-100 flex items-center justify-center mb-4">
            <i className="ri-question-line text-secondary-500 text-2xl"></i>
          </div>
          <h1 className="font-heading text-xl font-semibold text-foreground-900 mb-2">分类不存在</h1>
          <p className="text-sm text-foreground-500 mb-6">该分类未找到，请返回首页浏览其他内容。</p>
          <Link
            to="/"
            className="px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors"
          >
            返回首页
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const categorySchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": `${currentCategory.name} - SP完全指南`,
    "description": currentCategory.description,
    "url": `https://sp-guide.readdy.ai/category/${currentCategory.slug}`,
    "inLanguage": "zh-CN",
    "isPartOf": {
      "@type": "WebSite",
      "name": "SP完全指南",
      "url": "https://sp-guide.readdy.ai/"
    }
  };

  return (
    <>
      <SEOHead
        title={`${currentCategory.name} - SP完全指南`}
        description={currentCategory.description}
        keywords={`打屁股sp,sp${currentCategory.name},sp知识`}
        canonicalUrl={`https://sp-guide.readdy.ai/category/${currentCategory.slug}`}
        ogTitle={`${currentCategory.name} - SP完全指南`}
        ogDescription={currentCategory.description}
      />
      <SchemaOrg data={categorySchema} />
      <ScrollToTop />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Category Header */}
          <section className="w-full bg-background-100 border-b border-background-200/60">
            <Breadcrumb
              items={[
                { label: "首页", path: "/" },
                { label: currentCategory.name },
              ]}
            />
            <div className="max-w-7xl mx-auto px-4 md:px-6 pb-10">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 rounded-xl bg-primary-100 flex items-center justify-center">
                  <i className={`${currentCategory.icon} text-primary-600 text-2xl`}></i>
                </div>
                <div>
                  <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                    {currentCategory.name}
                  </h1>
                  <p className="text-sm text-foreground-500 mt-1">{currentCategory.description}</p>
                </div>
              </div>
            </div>
          </section>

          {/* Category Tabs */}
          <section className="w-full px-4 md:px-6 py-6 border-b border-background-200/40 bg-background-50 sticky top-16 z-40">
            <div className="max-w-7xl mx-auto flex items-center gap-2 overflow-x-auto">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                    cat.slug === slug
                      ? "bg-primary-500 text-background-50"
                      : "bg-secondary-100 text-secondary-700 hover:bg-secondary-200"
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </section>

          {/* Article List */}
          <section className="w-full px-4 md:px-6 py-10 md:py-14">
            <div className="max-w-7xl mx-auto">
              {categoryArticles.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {categoryArticles.map((article) => (
                    <ArticleCard key={article.id} article={article} showCategory={false} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <div className="w-16 h-16 rounded-full bg-secondary-100 flex items-center justify-center mx-auto mb-4">
                    <i className="ri-article-line text-secondary-500 text-2xl"></i>
                  </div>
                  <p className="text-sm text-foreground-500">该分类暂无文章</p>
                </div>
              )}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}